import {useAnalytics, useMetrics} from '@sail/observability';
import {getEnvironment} from '@sail/utils';
import {useCallback, useMemo} from 'react';
import Registry from 'src/internal/Registry';
import RegistriesContext from 'src/internal/contexts/RegistriesContext';

import type {ReactElement, ReactNode} from 'react';
import type {Extra} from 'src/internal/types';
import type {Experiment, Flag, InnerExperiments, InnerFlags} from 'src/types';

type Props = {
  children: ReactNode;
};

export default function RegistriesProvider({children}: Props): ReactElement {
  // ----- Experiments section:

  const {track} = useAnalytics();

  const trackExperiment = useCallback(
    <TName extends Experiment>(
      name: TName,
      variant: InnerExperiments[TName],
      extra: Extra,
    ) => {
      track('preloaded_experiment_retrieved', {
        experiment_checked_by: 'sdk/access',
        experiment_retrieved: name,
        experiment_treatment_group: variant ?? 'control',
        ...extra,
      });
    },
    [track],
  );

  // ----- Flags section:

  const {increment} = useMetrics();
  const isMetricEnabled =
    typeof window !== 'undefined' &&
    (window as any).PRELOADED?.flags?.['enable_feature_flag_access_metrics'];

  const trackFlag = useCallback(
    <TName extends Flag>(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      name: TName,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      value: InnerFlags[TName],
      _extra: Extra,
    ) => {
      if (getEnvironment() === 'test' || isMetricEnabled) {
        increment('frontend.access.feature_flag.accesses_total', {
          flag: name,
          is_active: !!value,
          // Do not add additional tags here! It is important to keep the number
          // of time-series as small as possible. With these three tags the
          // cardinality is: {feature flags} x {true,false}
        });
      }
    },
    [increment, isMetricEnabled],
  );

  const registries = useMemo(
    () => ({
      experimentsRegistry: new Registry<InnerExperiments>(trackExperiment),
      flagsRegistry: new Registry<InnerFlags>(trackFlag),
    }),
    [trackExperiment, trackFlag],
  );

  return (
    <RegistriesContext.Provider value={registries}>
      {children}
    </RegistriesContext.Provider>
  );
}
